import uniqWith from 'lodash-es/uniqWith';
import type { IRegionType, IRegionValue } from '@/pages/intelligence/types/common';
import type {
  IRegion,
  IRegionCountry,
  IRegionValueDataOfRegionSelector,
} from '@/pages/intelligence/types/pcConsoleCompare';

/**
 * 处理异常级联国家地区数据,返回正常的数据
 * @param regions 接口返回的级联国家地区数据
 * @returns 处理重复数据后的 级联国家地区数据,比如 同一个大洲 返回了两个对象, 而且 两个大洲 下的国家 不同, 需要 把这两个大洲合并为一个大洲,国家取并集
 */
export function handleRegions(regions: IRegion[]): IRegion[] {
  if (!Array.isArray(regions)) return [];
  const handledRegions: IRegion[] = [];
  // 国家去重的comparator
  const countryComparator = (a: IRegionCountry, b: IRegionCountry) => a?.country_code === b?.country_code;

  // 遍历大洲
  for (const region of regions) {
    let regionTemp = handledRegions?.find((item) => item?.area_code === region?.area_code);
    if (!regionTemp) {
      // 首次遍历到当前大洲
      regionTemp = {
        ...region,
        // 对当前大洲下的国家 做去重处理
        country_set: uniqWith(region?.country_set, countryComparator),
      };
      handledRegions.push(regionTemp);
    } else {
      // 非首次遍历到当前大洲，表明之前的遍历 已存在当前大洲了（handledRegions数组中已存在当前大洲）
      // 合并已存在的当前大洲下的国家，和 当前遍历中的大洲 的国家
      let newCountries = [...regionTemp?.country_set, ...region?.country_set];
      // 对国家做去重处理
      newCountries = uniqWith(newCountries, countryComparator);
      regionTemp.country_set = newCountries;
    }
  }

  return handledRegions;
}

/**
 * 获取有效的国家筛选器value，防止错误value，导致国家筛选器组件异常
 * @param regionData 国脚筛选器value
 * @returns 有效的国家筛选器value
 */
export function getValidRegionsForRegionSelect(
  regionData?: IRegionValueDataOfRegionSelector,
): IRegionValueDataOfRegionSelector {
  const type = regionData?.type || 'market';
  const regions = regionData?.regions;
  const markets = regionData?.markets;
  return {
    type,
    regions: Array.isArray(regions) && regions.length > 0 ? regions : [],
    markets: Array.isArray(markets) && markets.length > 0 ? markets : ['global'],
  };
}

/**
 * 过滤掉 继承未来的国家筛选器value中，国家筛选器 选项数据中 不包含的 地区、国家，仅保留当前选项数据中包含的地区、国家
 * @param regionsCountries 国脚筛选器 选项数据
 * @param regionData 国家筛选器的value
 * @returns 国家筛选器有效的value，因为value可能是从别的页面继承过来的，所以需要过滤掉 国家筛选器选项数据中 不包含的 地区、国家
 */
export function getValidRegionsForRegionSelectFromExtendsValue(
  regionsCountries: IRegion[],
  regionData?: IRegionValueDataOfRegionSelector,
): IRegionValueDataOfRegionSelector {
  // 异常情况 直接返回global
  if (!Array.isArray(regionsCountries) || !regionData) {
    return {
      type: 'market',
      regions: [],
      markets: ['global'],
    };
  }
  // 有选择global，返回数据干净的global
  if (regionData?.markets?.includes('global')) {
    return {
      type: 'market',
      regions: [],
      markets: ['global'],
    };
  }
  // 有选择global_wo_china，返回数据干净的global_wo_china
  if (regionData?.markets?.includes('global_wo_china')) {
    return {
      type: 'market',
      regions: [],
      markets: ['global_wo_china'],
    };
  }
  // 存储全选的地区，利用set自带去重
  const regionsSet = new Set();
  // 存储勾选的国家，利用set自带去重
  const marketsSet = new Set();
  // 判断region type是 region 还是 market的标识
  let isRegion = true;
  // 遍历地区
  for (const regionCountries of regionsCountries) {
    const areaCode = regionCountries?.area_code;
    const countries = regionCountries?.country_set;
    // 当前地区下的国家 被勾选的数量
    let checkedCountriesCount = 0;
    if (!areaCode) continue;
    // 传入的国家筛选器value 的 regions 包含当前地区，则把当前地区 add 进 regionsSet
    if (regionData?.regions?.includes(areaCode)) {
      regionsSet.add(areaCode);
    }
    if (!countries?.length) continue;
    // 遍历当前地区下的国家
    for (const country of countries) {
      const countryCode = country?.country_code;
      if (!countryCode) continue;
      // 传入的国家筛选器value 的 markets 包含当前国家，则把当前国家 add 进 marketsSet
      if (regionData?.markets?.includes(countryCode)) {
        marketsSet.add(countryCode);
        // 并且当前地区下 已勾选的国家数量加1
        checkedCountriesCount += 1;
        continue;
      }
    }
    /**
     * if 当前地区下的国家数量 === 当前地区下已勾选的国家数量，则说明当前地区 全选了，需要把当前地区add 进 regionsSet
     * else 把当前地区从regionsSet中移除
     */
    if (checkedCountriesCount === countries.length) {
      regionsSet.add(areaCode);
    } else {
      // 当前地区下的国家没有全选，并且当前地区下已勾选的国家数量 > 0，说明 国家筛选器的type，就是 market了，不是region，仅当勾选的只有纯地区的时候 才是region
      if (checkedCountriesCount > 0) {
        isRegion = false;
      }
      regionsSet.delete(areaCode);
    }
  }
  return {
    type: isRegion ? 'region' : 'market',
    regions: Array.from(regionsSet) as IRegionValue,
    markets: Array.from(marketsSet) as IRegionValue,
  };
}

/**
 * 把国家筛选器返回的value 转换为 接口需要的格式
 * 国家字母全小写，只有global时，regionType传region，其余情况都是market
 * @param params 国家筛选器组件的value，onChange 回传的value
 * @returns 接口需要的格式
 */
export function getRegionsReqParamsByRegionSelectorValue(params?: IRegionValueDataOfRegionSelector): {
  regionType: IRegionType;
  regions: IRegionValue;
} {
  const result = {
    regionType: 'region',
    regions: ['global'],
  };
  const markets = params?.markets || [];
  // 有选global，直接返回global
  if (markets.includes('global')) return result;
  // 有选global_wo_china，直接返回global_wo_china
  if (markets.includes('global_wo_china')) {
    result.regions = ['global_wo_china'];
    return result;
  }
  // 数据异常，返回默认的global
  if (!Array.isArray(markets)) return result;
  // 存储临时转换的国家数据
  const regionsTemp: string[] = [];
  for (const market of markets) {
    if (!market?.trim()) continue;
    if (regionsTemp.includes(market)) continue;
    regionsTemp.push(market);
  }
  // 数据异常 返回默认的global
  if (regionsTemp?.length === 0) return result;

  // 返回转换后的国家数据，只要不是global、global_wo_china，region_type 就传market
  result.regionType = 'market';
  result.regions = regionsTemp;
  return result;
}
